import React, { useState, useRef, useEffect, useMemo } from "react"
import { createRoot } from "react-dom/client"
import { useChat } from "@ai-sdk/react"
import { DefaultChatTransport } from "ai"
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Generate a unique session ID
function generateSessionId(): string {
  return crypto.randomUUID()
}

// Icons as SVG components
const MessageCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
)

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18"></path>
    <path d="m6 6 12 12"></path>
  </svg>
)

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 2-7 20-4-9-9-4Z"></path>
    <path d="M22 2 11 13"></path>
  </svg>
)

const AlertCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" x2="12" y1="8" y2="12"></line>
    <line x1="12" x2="12.01" y1="16" y2="16"></line>
  </svg>
)


// Get API URL from script tag or default to script source origin
const getApiUrl = (): string => {
  const script = document.currentScript as HTMLScriptElement

  // First priority: explicit data-api-url attribute
  if (script?.dataset?.apiUrl) {
    return script.dataset.apiUrl
  }

  // Second priority: extract origin from where this script was loaded
  if (script?.src) {
    try {
      const url = new URL(script.src)
      return url.origin
    } catch (e) {
      console.error('Failed to parse script URL:', e)
    }
  }

  // Fallback: hardcoded Vercel app URL
  return 'https://haz-ppm-armature-ai-chatbot-nine.vercel.app'
}

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [sessionId, setSessionId] = useState<string>(() => generateSessionId())
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null)

  const apiUrl = getApiUrl()

  // Create transport with sessionId in body
  const transport = useMemo(
    () => new DefaultChatTransport({ api: `${apiUrl}/api/chat`, body: { sessionId } }),
    [apiUrl, sessionId]
  )

  const { messages, sendMessage, status, error, setMessages } = useChat({
    transport,
  })

  // Auto-scroll to bottom when messages change or loading starts
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, status])

  // Reset chat after 3 minutes of widget being closed
  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    if (!isOpen) {
      closeTimerRef.current = setTimeout(() => {
        setMessages([])
        setInputValue("")
        setSessionId(generateSessionId()) // Generate new session ID for new conversation
      }, 3 * 60 * 1000)
    }

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
  }, [isOpen, setMessages])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim() || status === "streaming") return

    sendMessage({ text: inputValue })
    setInputValue("")
  }

  return (
    <>
      <style>{`
        @keyframes slideInFromBottom {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-25%);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .chat-widget-button {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background-color: #8BC53F;
          color: white;
          border: none;
          cursor: pointer;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s, background-color 0.2s;
          z-index: 999999;
        }

        .chat-widget-button:hover {
          background-color: #7AB535;
          transform: scale(1.05);
        }

        .chat-widget-window {
          position: fixed;
          bottom: 96px;
          right: 24px;
          width: 380px;
          height: 500px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: slideInFromBottom 0.3s ease-out;
          z-index: 999999;
          border: 1px solid rgba(45, 74, 62, 0.2);
        }

        .chat-widget-header {
          background-color: #8BC53F;
          color: white;
          padding: 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }

        .chat-widget-header-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .chat-widget-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chat-widget-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }

        .chat-widget-subtitle {
          font-size: 12px;
          opacity: 0.8;
          margin: 2px 0 0 0;
        }

        .chat-widget-close {
          background: transparent;
          border: none;
          color: white;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chat-widget-close:hover {
          background-color: rgba(255, 255, 255, 0.2);
        }

        .chat-widget-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          min-height: 0;
        }

        .chat-widget-empty {
          text-align: center;
          color: #71717a;
          padding: 32px 0;
        }

        .chat-widget-empty-icon {
          margin: 0 auto 12px;
          opacity: 0.4;
          color: #8BC53F;
        }

        .chat-widget-empty-text {
          font-size: 14px;
          margin: 0;
        }

        .chat-widget-empty-subtext {
          font-size: 12px;
          margin: 4px 0 0 0;
        }

        .chat-widget-message {
          display: flex;
        }

        .chat-widget-message.user {
          justify-content: flex-end;
        }

        .chat-widget-message.assistant {
          justify-content: flex-start;
        }

        .chat-widget-message-content {
          max-width: 80%;
          border-radius: 16px;
          padding: 10px 16px;
        }

        .chat-widget-message.user .chat-widget-message-content {
          background-color: #8BC53F;
          color: white;
        }

        .chat-widget-message.assistant .chat-widget-message-content {
          background-color: white;
          box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          border: 1px solid rgba(45, 74, 62, 0.1);
        }

        .chat-widget-message-text {
          font-size: 14px;
          line-height: 1.5;
          white-space: pre-wrap;
          margin: 0;
        }

        .chat-widget-loader {
          display: flex;
          gap: 4px;
        }

        .chat-widget-loader-dot {
          width: 8px;
          height: 8px;
          background-color: #8BC53F;
          border-radius: 50%;
          animation: bounce 1s infinite;
        }

        .chat-widget-loader-dot:nth-child(1) {
          animation-delay: 0ms;
        }

        .chat-widget-loader-dot:nth-child(2) {
          animation-delay: 150ms;
        }

        .chat-widget-loader-dot:nth-child(3) {
          animation-delay: 300ms;
        }

        .chat-widget-streaming-indicator {
          display: inline-flex;
          gap: 4px;
          margin-left: 4px;
          vertical-align: middle;
        }

        .chat-widget-streaming-dot {
          width: 6px;
          height: 6px;
          background-color: #8BC53F;
          border-radius: 50%;
          animation: pulse 1.5s infinite;
        }

        .chat-widget-streaming-dot:nth-child(1) {
          animation-delay: 0ms;
        }

        .chat-widget-streaming-dot:nth-child(2) {
          animation-delay: 150ms;
        }

        .chat-widget-streaming-dot:nth-child(3) {
          animation-delay: 300ms;
        }

        .chat-widget-error {
          display: flex;
          justify-content: center;
        }

        .chat-widget-error-content {
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 12px 16px;
          max-width: 90%;
        }

        .chat-widget-error-inner {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }

        .chat-widget-error-icon {
          color: #dc2626;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .chat-widget-error-title {
          font-size: 14px;
          font-weight: 500;
          color: #991b1b;
          margin: 0;
        }

        .chat-widget-error-message {
          font-size: 12px;
          color: #dc2626;
          margin: 4px 0 0 0;
        }

        .chat-widget-input-form {
          padding: 16px;
          border-top: 1px solid rgba(45, 74, 62, 0.1);
          background-color: white;
          flex-shrink: 0;
        }

        .chat-widget-input-container {
          display: flex;
          gap: 8px;
        }

        .chat-widget-input {
          flex: 1;
          padding: 10px 16px;
          border-radius: 24px;
          border: 1px solid rgba(45, 74, 62, 0.2);
          font-size: 14px;
          outline: none;
        }

        .chat-widget-input:focus {
          border-color: #8BC53F;
          box-shadow: 0 0 0 2px rgba(212, 165, 116, 0.2);
        }

        .chat-widget-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .chat-widget-submit {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #8BC53F;
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .chat-widget-submit:hover:not(:disabled) {
          background-color: #7AB535;
        }

        .chat-widget-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="chat-widget-button"
        aria-label="Open chat"
      >
        {isOpen ? <XIcon /> : <MessageCircleIcon />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-widget-window">
          {/* Header */}
          <div className="chat-widget-header">
            <div className="chat-widget-header-content">
              <div className="chat-widget-avatar">
                <MessageCircleIcon />
              </div>
              <div>
                <h3 className="chat-widget-title">Armature Assistant</h3>
                <p className="chat-widget-subtitle">Always here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="chat-widget-close"
            >
              <XIcon />
            </button>
          </div>

          {/* Messages */}
          <div className="chat-widget-messages">
            {messages.length === 0 && (
              <div className="chat-widget-empty">
                <div className="chat-widget-empty-icon">
                  <MessageCircleIcon />
                </div>
                <p className="chat-widget-empty-text">Start a conversation!</p>
                <p className="chat-widget-empty-subtext">Ask me anything you'd like to know.</p>
              </div>
            )}

            {messages.map((message, messageIndex) => {
              const isLastMessage = messageIndex === messages.length - 1
              const isStreaming = status === "streaming" && isLastMessage && message.role === "assistant"

              const hasTextContent = message.role === "assistant"
                ? message.parts.some((part) => part.type === "text" && part.text?.trim())
                : true

              if (message.role === "assistant" && !hasTextContent && !isStreaming) {
                return null
              }

              return (
                <div key={message.id} className={`chat-widget-message ${message.role}`}>
                  <div className="chat-widget-message-content">
                    {hasTextContent ? (
                      message.parts.map((part, index) => {
                        if (part.type === "text") {
                          return (
                            <div key={index} className="chat-widget-message-text">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  a: ({ href, children }) => {
                                    // Check if this is an auto-linked URL (link text equals the URL)
                                    const isAutoLinkedUrl = typeof children === 'string'
                                      ? children === href
                                      : Array.isArray(children) && children.length === 1 && children[0] === href

                                    // Prettify: strip protocol and trailing slash
                                    const displayText = isAutoLinkedUrl && href
                                      ? href.replace(/^https?:\/\//, '').replace(/\/$/, '')
                                      : children

                                    return (
                                      <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#8BC53F' }}>
                                        {displayText}
                                      </a>
                                    )
                                  },
                                  p: ({ children }) => <p style={{ margin: '0 0 0.5em 0' }}>{children}</p>,
                                  ul: ({ children }) => <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em', listStyleType: 'disc' }}>{children}</ul>,
                                  ol: ({ children }) => <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em', listStyleType: 'decimal' }}>{children}</ol>,
                                  li: ({ children }) => <li style={{ margin: '0.25em 0' }}>{children}</li>,
                                  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                }}
                              >
                                {part.text || ''}
                              </ReactMarkdown>
                            </div>
                          )
                        }
                        return null
                      })
                    ) : (
                      <div className="chat-widget-loader">
                        <div className="chat-widget-loader-dot" />
                        <div className="chat-widget-loader-dot" />
                        <div className="chat-widget-loader-dot" />
                      </div>
                    )}
                    {isStreaming && hasTextContent && (
                      <div className="chat-widget-streaming-indicator">
                        <div className="chat-widget-streaming-dot" />
                        <div className="chat-widget-streaming-dot" />
                        <div className="chat-widget-streaming-dot" />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            {error && (
              <div className="chat-widget-error">
                <div className="chat-widget-error-content">
                  <div className="chat-widget-error-inner">
                    <div className="chat-widget-error-icon">
                      <AlertCircleIcon />
                    </div>
                    <div>
                      <p className="chat-widget-error-title">Unable to send message</p>
                      <p className="chat-widget-error-message">
                        {(() => {
                          const messageLower = error.message.toLowerCase()

                          if (
                            messageLower.includes('rate_limit') ||
                            messageLower.includes('rate limit') ||
                            messageLower.includes('too many requests') ||
                            messageLower.includes('request too large') ||
                            messageLower.includes('tokens per min') ||
                            messageLower.includes('tpm') ||
                            messageLower.includes('token limit') ||
                            (messageLower.includes('quota') && messageLower.includes('exceeded')) ||
                            (messageLower.includes('limit') && messageLower.includes('requested'))
                          ) {
                            return 'The service is experiencing high demand. Please try again in a moment.'
                          }

                          try {
                            const parsed = JSON.parse(error.message)
                            if (parsed?.error?.message) {
                              const apiMessage = parsed.error.message.toLowerCase()
                              if (
                                apiMessage.includes('rate_limit') ||
                                apiMessage.includes('request too large') ||
                                apiMessage.includes('token')
                              ) {
                                return 'The service is experiencing high demand. Please try again in a moment.'
                              }
                              return parsed.error.message
                            }
                            if (parsed?.error?.code === 'rate_limit_exceeded') {
                              return 'The service is experiencing high demand. Please try again in a moment.'
                            }
                          } catch {
                            // Not JSON, continue
                          }

                          return 'An error occurred. Please try again.'
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="chat-widget-input-form">
            <div className="chat-widget-input-container">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                disabled={status === "streaming"}
                className="chat-widget-input"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || status === "streaming"}
                className="chat-widget-submit"
              >
                <SendIcon />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  )
}

// Auto-initialize when script loads
function init() {
  // Create container with shadow DOM to isolate styles
  const container = document.createElement('div')
  container.id = 'chat-widget-root'
  document.body.appendChild(container)

  const root = createRoot(container)
  root.render(<ChatWidget />)
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
